// ==============================================================
// Notifications Redux Slice
// Professional notification state management with optimistic updates
// ==============================================================

import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@/redux/store";

// ==============================================================
// Types
// ==============================================================

export type NotificationType =
  | "CALL"
  | "VISITOR"
  | "TEAM"
  | "SYSTEM"
  | "ALERT"
  | "MEETING"
  | "PAYMENT";

export type NotificationPriority = "LOW" | "NORMAL" | "HIGH" | "URGENT";

export interface Notification {
  id: string;
  user_id: string;
  company_id: string | null;
  type: NotificationType;
  title: string;
  message: string;
  action_url?: string | null;
  is_read: boolean;
  read_at?: string | null;
  priority: NotificationPriority;
  metadata?: Record<string, unknown>;
  created_at: string;
}

export interface NotificationPreferences {
  email_enabled: boolean;
  push_enabled: boolean;
  in_app_enabled: boolean;
  email_frequency: "REALTIME" | "DAILY" | "WEEKLY" | "NEVER";
  preferences: Record<string, boolean>;
}

interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  offset: number;
  limit: number;
  filter: "all" | "unread" | "read";
  typeFilter: NotificationType | "all";
  preferences: NotificationPreferences | null;
  preferencesLoading: boolean;
  preferencesSaving: boolean;
  lastFetchedAt: number | null;
}

// ==============================================================
// Initial State
// ==============================================================

const initialState: NotificationsState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  loadingMore: false,
  error: null,
  hasMore: true,
  offset: 0,
  limit: 20,
  filter: "all",
  typeFilter: "all",
  preferences: null,
  preferencesLoading: false,
  preferencesSaving: false,
  lastFetchedAt: null,
};

// ==============================================================
// Async Thunks
// ==============================================================

// Import will be done dynamically to avoid circular deps
const getApiService = async () => {
  const { notificationsAPI } = await import("@/lib/services/api.service");
  return notificationsAPI;
};

export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async (
    params: {
      offset?: number;
      limit?: number;
      is_read?: boolean;
      type?: NotificationType;
      refresh?: boolean;
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const api = await getApiService();
      const result = await api.getAll({
        offset: params.offset ?? 0,
        limit: params.limit ?? 20,
        is_read: params.is_read,
        type: params.type,
      });
      return {
        ...result,
        refresh: params.refresh ?? false,
        offset: params.offset ?? 0,
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to fetch notifications"
      );
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  "notifications/fetchUnreadCount",
  async (_, { rejectWithValue }) => {
    try {
      const api = await getApiService();
      const result = await api.getUnreadCount();
      return result.count;
    } catch (error: any) {
      // Preserve error structure for proper handling (status codes, etc.)
      return rejectWithValue({
        message: error?.response?.data?.message || error.message || "Failed to fetch unread count",
        statusCode: error?.response?.status || error?.response?.data?.statusCode || error?.statusCode,
        response: error?.response,
      });
    }
  }
);

export const markAsRead = createAsyncThunk(
  "notifications/markAsRead",
  async (id: string, { rejectWithValue }) => {
    try {
      const api = await getApiService();
      const result = await api.markAsRead(id);
      return result.notification;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to mark as read"
      );
    }
  }
);

export const markAllAsRead = createAsyncThunk(
  "notifications/markAllAsRead",
  async (_, { rejectWithValue }) => {
    try {
      const api = await getApiService();
      await api.markAllAsRead();
      return true;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to mark all as read"
      );
    }
  }
);

export const deleteNotification = createAsyncThunk(
  "notifications/deleteNotification",
  async (id: string, { rejectWithValue }) => {
    try {
      const api = await getApiService();
      await api.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to delete notification"
      );
    }
  }
);

export const clearAllNotifications = createAsyncThunk(
  "notifications/clearAllNotifications",
  async (_, { rejectWithValue }) => {
    try {
      const api = await getApiService();
      await api.clearAll();
      return true;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to clear notifications"
      );
    }
  }
);

export const fetchNotificationPreferences = createAsyncThunk(
  "notifications/fetchPreferences",
  async (_, { rejectWithValue }) => {
    try {
      const { userSettingsAPI } = await import("@/lib/services/api.service");
      const result = await userSettingsAPI.getNotificationPreferences();
      return result.preferences;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to fetch preferences"
      );
    }
  }
);

export const updateNotificationPreferences = createAsyncThunk(
  "notifications/updatePreferences",
  async (preferences: Partial<NotificationPreferences>, { rejectWithValue }) => {
    try {
      const { userSettingsAPI } = await import("@/lib/services/api.service");
      const result = await userSettingsAPI.updateNotificationPreferences(preferences);
      return result.preferences;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || "Failed to update preferences"
      );
    }
  }
);

// ==============================================================
// Slice
// ==============================================================

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    // Add new notification (from real-time updates)
    addNotification: (state, action: PayloadAction<Notification>) => {
      // Check for duplicates
      const exists = state.notifications.some((n) => n.id === action.payload.id);
      if (!exists) {
        state.notifications.unshift(action.payload);
        if (!action.payload.is_read) {
          state.unreadCount += 1;
        }
      }
    },

    // Update notification (from real-time updates)
    updateNotification: (state, action: PayloadAction<Notification>) => {
      const index = state.notifications.findIndex((n) => n.id === action.payload.id);
      if (index !== -1) {
        const wasUnread = !state.notifications[index].is_read;
        const isNowRead = action.payload.is_read;
        state.notifications[index] = action.payload;
        if (wasUnread && isNowRead) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      }
    },

    // Remove notification (from real-time updates)
    removeNotification: (state, action: PayloadAction<string>) => {
      const index = state.notifications.findIndex((n) => n.id === action.payload);
      if (index !== -1) {
        if (!state.notifications[index].is_read) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        state.notifications.splice(index, 1);
      }
    },

    // Set filter
    setFilter: (state, action: PayloadAction<"all" | "unread" | "read">) => {
      state.filter = action.payload;
    },

    // Set type filter
    setTypeFilter: (state, action: PayloadAction<NotificationType | "all">) => {
      state.typeFilter = action.payload;
    },

    // Reset state
    resetNotifications: () => initialState,

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Set unread count (for badge updates)
    setUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = action.payload;
    },

    // Optimistic mark as read
    optimisticMarkAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find((n) => n.id === action.payload);
      if (notification && !notification.is_read) {
        notification.is_read = true;
        notification.read_at = new Date().toISOString();
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },

    // Optimistic mark all as read
    optimisticMarkAllAsRead: (state) => {
      state.notifications.forEach((n) => {
        if (!n.is_read) {
          n.is_read = true;
          n.read_at = new Date().toISOString();
        }
      });
      state.unreadCount = 0;
    },

    // Optimistic delete
    optimisticDelete: (state, action: PayloadAction<string>) => {
      const index = state.notifications.findIndex((n) => n.id === action.payload);
      if (index !== -1) {
        if (!state.notifications[index].is_read) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        state.notifications.splice(index, 1);
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch notifications
    builder
      .addCase(fetchNotifications.pending, (state, action) => {
        if (action.meta.arg?.refresh || action.meta.arg?.offset === 0) {
          state.loading = true;
        } else {
          state.loadingMore = true;
        }
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        const { notifications, total, refresh, offset } = action.payload;

        if (refresh || offset === 0) {
          state.notifications = notifications;
        } else {
          // Append new notifications, avoiding duplicates
          const existingIds = new Set(state.notifications.map((n) => n.id));
          const newNotifications = notifications.filter(
            (n: Notification) => !existingIds.has(n.id)
          );
          state.notifications = [...state.notifications, ...newNotifications];
        }

        state.offset = offset + notifications.length;
        state.hasMore = state.notifications.length < total;
        state.loading = false;
        state.loadingMore = false;
        state.lastFetchedAt = Date.now();
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        state.error = action.payload as string;
      });

    // Fetch unread count
    builder.addCase(fetchUnreadCount.fulfilled, (state, action) => {
      state.unreadCount = action.payload;
    });

    // Mark as read
    builder
      .addCase(markAsRead.fulfilled, (state, action) => {
        const notification = state.notifications.find((n) => n.id === action.payload.id);
        if (notification) {
          const wasUnread = !notification.is_read;
          Object.assign(notification, action.payload);
          if (wasUnread && action.payload.is_read) {
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          }
        }
      })
      .addCase(markAsRead.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Mark all as read
    builder
      .addCase(markAllAsRead.fulfilled, (state) => {
        state.notifications.forEach((n) => {
          n.is_read = true;
          n.read_at = new Date().toISOString();
        });
        state.unreadCount = 0;
      })
      .addCase(markAllAsRead.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Delete notification
    builder
      .addCase(deleteNotification.fulfilled, (state, action) => {
        const index = state.notifications.findIndex((n) => n.id === action.payload);
        if (index !== -1) {
          if (!state.notifications[index].is_read) {
            state.unreadCount = Math.max(0, state.unreadCount - 1);
          }
          state.notifications.splice(index, 1);
        }
      })
      .addCase(deleteNotification.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Clear all notifications
    builder
      .addCase(clearAllNotifications.fulfilled, (state) => {
        state.notifications = [];
        state.unreadCount = 0;
      })
      .addCase(clearAllNotifications.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Notification preferences
    builder
      .addCase(fetchNotificationPreferences.pending, (state) => {
        state.preferencesLoading = true;
      })
      .addCase(fetchNotificationPreferences.fulfilled, (state, action) => {
        state.preferences = action.payload;
        state.preferencesLoading = false;
      })
      .addCase(fetchNotificationPreferences.rejected, (state) => {
        state.preferencesLoading = false;
      });

    builder
      .addCase(updateNotificationPreferences.pending, (state) => {
        state.preferencesSaving = true;
      })
      .addCase(updateNotificationPreferences.fulfilled, (state, action) => {
        state.preferences = action.payload;
        state.preferencesSaving = false;
      })
      .addCase(updateNotificationPreferences.rejected, (state) => {
        state.preferencesSaving = false;
      });
  },
});

// ==============================================================
// Exports
// ==============================================================

export const {
  addNotification,
  updateNotification,
  removeNotification,
  setFilter,
  setTypeFilter,
  resetNotifications,
  clearError,
  setUnreadCount,
  optimisticMarkAsRead,
  optimisticMarkAllAsRead,
  optimisticDelete,
} = notificationsSlice.actions;

// Selectors
export const selectNotifications = (state: RootState) => state.notifications.notifications;
export const selectUnreadCount = (state: RootState) => state.notifications.unreadCount;
export const selectNotificationsLoading = (state: RootState) => state.notifications.loading;
export const selectNotificationsError = (state: RootState) => state.notifications.error;
export const selectNotificationsFilter = (state: RootState) => state.notifications.filter;
export const selectNotificationsTypeFilter = (state: RootState) => state.notifications.typeFilter;
export const selectNotificationsHasMore = (state: RootState) => state.notifications.hasMore;
export const selectNotificationsLoadingMore = (state: RootState) => state.notifications.loadingMore;
export const selectNotificationPreferences = (state: RootState) => state.notifications.preferences;
export const selectNotificationPreferencesLoading = (state: RootState) =>
  state.notifications.preferencesLoading;
export const selectNotificationPreferencesSaving = (state: RootState) =>
  state.notifications.preferencesSaving;

// Derived selectors
export const selectFilteredNotifications = (state: RootState) => {
  const { notifications, filter, typeFilter } = state.notifications;

  return notifications.filter((n) => {
    // Apply read/unread filter
    if (filter === "unread" && n.is_read) return false;
    if (filter === "read" && !n.is_read) return false;

    // Apply type filter
    if (typeFilter !== "all" && n.type !== typeFilter) return false;

    return true;
  });
};

export const selectNotificationsByType = (state: RootState) => {
  const notifications = state.notifications.notifications;
  const grouped: Record<NotificationType, Notification[]> = {
    CALL: [],
    VISITOR: [],
    TEAM: [],
    SYSTEM: [],
    ALERT: [],
    MEETING: [],
    PAYMENT: [],
  };

  notifications.forEach((n) => {
    if (grouped[n.type]) {
      grouped[n.type].push(n);
    }
  });

  return grouped;
};

export default notificationsSlice.reducer;
